import React from 'react'
import {
  concat,
  each,
  find,
  isEmpty,
  join,
  map,
  reduce,
  uniq,
  uniqBy,
  sortBy
} from 'lodash'
import Moment from 'moment'
import { extendMoment } from 'moment-range'

import PriorityCell from '../Tables/Ticket/PriorityCell'
import { saveLocalStorageItem, loadLocalStorageItem } from '../../utilities/LocalStorage'

import RightClickMenu from './CustomComponents/RightClickMenu'

const moment = extendMoment(Moment)

const resourcesContainer = {
  resourceBar: [],
  dispatcher: loadLocalStorageItem('Dispatcher.resources', 'dispatcher') || [{}],
  resourceBarValues: loadLocalStorageItem('Dispatcher.resources', 'barValues') || [],

  addToResourceBar (resource) {
    let teamOptions = []
    let queueOptions = []

    if (resource.type === 'Team') {
      teamOptions = {
        users: map(resource.users, ({ id, name, outlook_authorized, timezone, has_queue }) => ({
          value: id,
          label: name,
          type: 'user',
          timezone,
          outlook_authorized,
          has_queue
        })),
        value: `team_${resource.id}`,
        label: `Team: ${resource.name}`,
        type: 'team'
      }
    } else {
      queueOptions = {
        users: map(resource.users, ({ id, name, outlook_authorized, timezone, has_queue }) => ({
          value: id,
          label: name,
          type: 'user',
          timezone,
          outlook_authorized,
          has_queue
        })),
        value: `queue_${resource.id}`,
        label: `Queue: ${resource.name}`,
        queue_id: resource.id,
        type: 'queue'
      }
    }

    const userOptions = reduce(resource.users, (accumulator, user) => {
      const userOption = {
        value: user.id,
        label: user.name,
        timezone: user.timezone,
        type: 'user',
        outlook_authorized: user.outlook_authorized,
        has_queue: user.has_queue
      }

      return concat(accumulator, userOption)
    }, [])

    const options = concat(queueOptions, userOptions, teamOptions)

    each(
      uniq(options), (option) => {
        if (!find(this.resourceBar, { value: option.value })) {
          this.resourceBar.push(option)
        }
      }
    )
  },

  updateDispatcher (resources) {
    const newDispatcherResources = reduce(resources, (accumulator, resource) => {
      if (resource.type === 'user') {
        accumulator.push({
          resourceId: resource.value,
          resourceTitle: resource.label,
          resourceTimezone: resource.timezone,
          resourceOutlookAuthorized: resource.outlook_authorized,
          resourceHasQueue: resource.has_queue
        })
      }

      if (resource.type === 'queue') {
        each(resource.users, user => accumulator.push({
          resourceId: user.value,
          resourceTitle: user.label,
          resourceTimezone: user.timezone,
          resourceOutlookAuthorized: user.outlook_authorized,
          resourceHasQueue: user.has_queue
        }))
      }

      if (resource.type === 'team') {
        each(resource.users, user => accumulator.push({
          resourceId: user.value,
          resourceTitle: user.label,
          resourceTimezone: user.timezone,
          resourceOutlookAuthorized: user.outlook_authorized,
          resourceHasQueue: user.has_queue
        }))
      }

      return uniqBy(accumulator, 'resourceId')
    }, [])

    this.dispatcher = isEmpty(resources) ? [{}] : newDispatcherResources

    saveLocalStorageItem('Dispatcher.resources', 'dispatcher', this.dispatcher)
  }
}

const eventsFilterContainer = {
  userIds: loadLocalStorageItem('Dispatcher.eventsFilter', 'userIds') || [],
  queueIds: loadLocalStorageItem('Dispatcher.eventsFilter', 'queueIds') || [],
  teamIds: loadLocalStorageItem('Dispatcher.eventsFilter', 'teamIds') || [],

  fetchFromResources (resources) {
    if (isEmpty(resources)) {
      this.userIds = []
      this.queueIds = []
      this.teamIds = []

      return
    }

    const newUserIds = []
    const newQueueIds = []
    const newTeamIds = []

    each(resources, (resource) => {
      if (resource.type === 'user') { newUserIds.push(resource.value) }
      if (resource.type === 'queue') {
        each(resource.users, user => newUserIds.push(user.value))
        newQueueIds.push(resource.value)
      }

      if (resource.type === 'team') {
        each(resource.users, user => newUserIds.push(user.value))
        newTeamIds.push(resource.value)
      }
    })

    this.userIds = uniq(newUserIds)
    saveLocalStorageItem('Dispatcher.eventsFilter', 'userIds', this.userIds)

    this.queueIds = uniq(newQueueIds)
    saveLocalStorageItem('Dispatcher.eventsFilter', 'queueIds', this.queueIds)

    this.teamIds = uniq(newTeamIds)
    saveLocalStorageItem('Dispatcher.eventsFilter', 'teamIds', this.teamIds)
  },

  toString () {
    const eventsFilterStringSegments = ['cr,pj,pt,tk'] // add other calendar types

    each(this.userIds, (id) => {
      eventsFilterStringSegments.push(`u(${id})`)
    })

    return join(eventsFilterStringSegments, ',')
  },

  isEmpty () {
    return (isEmpty(this.userIds) && isEmpty(this.queueIds))
  }
}

const calendarDefaultsContainer = {
  currentView: 'day',
  defaultDate: new Date()
}

const columns = (contextMenuStatuses, contextMenuPriorities) => (
  [
    {
      text: 'ID',
      accessor: 'id',
      Cell: row => (
        <a href={`/tickets/${row.original.id}`} target="_blank" rel="noopener noreferrer">#{row.original.id}</a>
      )
    },
    {
      text: 'Title',
      accessor: 'title'
    },
    {
      text: 'Priority',
      accessor: 'priority_id',
      className: 'flex flex--centered',
      Cell: (row) => {
        const trigger = <PriorityCell ticket={row.original} keyClient={row.original.key_client} />

        return (
          <RightClickMenu
            trigger={trigger}
            menuItems={contextMenuPriorities}
            mainObjectId={row.original.id}
            objectType="ticket-priority"
            menuStyle={{ width: '100%', height: '100%', textAlign: 'center' }}
          />
        )
      }
    },
    {
      text: 'Client',
      accessor: 'client_name'
    },
    {
      text: 'Status',
      accessor: 'status_text',
      Cell: row => (
        <RightClickMenu
          trigger={row.value}
          menuItems={contextMenuStatuses}
          mainObjectId={row.original.id}
          objectType="ticket-status"
          style={{ width: '100%', height: '100%' }}
        />
      )
    },
    {
      text: 'Queue',
      accessor: 'queue_name'
    },
    {
      text: 'Assignee',
      accessor: 'assignee_name'
    },
    {
      text: 'Age (days)',
      accessor: 'created_at',
      Cell: row => (
        row.original.age
      )
    },
    {
      text: 'Created',
      accessor: 'created_at',
      Cell: row => (
        moment(row.value).format('DD MMM YYYY, hh:mmA')
      )
    }
  ]
)

const filterOptions = (queues, statusNames, clients, regions, users) => ([
  {
    value: 'Assignee',
    subject: 'assignee_user_id',
    type: 'is',
    values: [{ label: 'Unassigned', value: 'Not yet assigned' }].concat(uniqBy(sortBy(map(users, user => ({ label: user.name, value: user.id })), 'label'), 'value'))
  },
  {
    value: 'Queue',
    subject: 'queue_id',
    type: 'is',
    values: sortBy(map(queues, queue => ({ label: queue.name, value: queue.id })), 'label')
  },
  {
    value: 'Key Client',
    subject: 'key_client',
    type: 'is',
    values: [{ value: false, label: 'No' }, { value: true, label: 'Yes' }]
  },
  {
    value: 'Status',
    subject: 'status_id',
    type: 'is',
    values: statusNames
  },
  {
    value: 'Priority',
    subject: 'priority_id',
    type: 'is',
    values: [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }, { value: 5 }]
  },
  {
    value: 'Client',
    type: 'is',
    subject: 'client_id',
    values: sortBy(map(clients, client => ({ label: client.full_name, value: client.id })), 'label')
  },
  {
    value: 'Region',
    type: 'is',
    subject: 'region_id',
    values: sortBy(regions, 'label')
  }
])

const ticketsTableTabs = (queueIds, currentUserId) => ([
  {
    subject: 'parent_status', type: 'is', value: 'all', text: 'All'
  },
  {
    subject: 'parent_status', type: 'is', value: 'new_unassigned', text: 'New'
  },
  {
    subject: 'parent_status', type: 'is', value: 'for_dispatcher', text: 'Action Required'
  },
  {
    subject: 'my_queue_current', type: 'is', value: queueIds, text: 'Current'
  },
  {
    subject: 'my_tickets_current', type: 'is', value: currentUserId, text: 'My Tickets'
  }
])

export {
  eventsFilterContainer,
  resourcesContainer,
  calendarDefaultsContainer,
  columns,
  filterOptions,
  ticketsTableTabs
}
